import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { useUiStore } from '../../stores/uiStore.js';
import { LoadingSpinner } from '../ui/index.js';
import type { Day, Scene, Pin, Location } from '../../types/index.js';

interface SceneCardProps {
  scene: Scene;
}

export function SceneCard({ scene }: SceneCardProps) {
  const qc = useQueryClient();
  const { activeScene, setActiveScene, activeProject, flyTo } = useMapStore();
  const { openModal } = useUiStore();
  const isActive = activeScene?.id === scene.id;
  const [dayPickerOpen, setDayPickerOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setDayPickerOpen(false);
        setLibraryOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const { data: days } = useQuery({
    queryKey: ['days', activeProject?.id],
    queryFn: async () => {
      if (!activeProject) return [];
      const result = await api.get<{ days: Day[] }>(`/days?projectId=${activeProject.id}`);
      return result.success ? result.data.days : [];
    },
    enabled: !!activeProject && dayPickerOpen,
  });

  // Pins for this scene (only when active)
  const { data: pinsData, isLoading: pinsLoading } = useQuery({
    queryKey: ['pins', scene.id],
    queryFn: async () => {
      const result = await api.get<{ pins: Pin[] }>(`/pins?sceneId=${scene.id}&limit=200`);
      return result.success ? result.data : { pins: [] };
    },
    enabled: isActive,
  });

  // Library locations for adding (only when picker open)
  const { data: libraryLocs } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const result = await api.get<{ locations: Location[] }>('/locations');
      return result.success ? result.data.locations : [];
    },
    enabled: isActive && libraryOpen,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/scenes/${scene.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scenes', scene.projectId] });
      if (isActive) setActiveScene(null);
      toast.success('Scene deleted');
    },
  });

  const addToDayMutation = useMutation({
    mutationFn: (dayId: string) => api.post(`/days/${dayId}/scenes`, { sceneId: scene.id }),
    onSuccess: (result, dayId) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['day-scenes', dayId] });
      toast.success('Scene added to day');
      setDayPickerOpen(false);
    },
  });

  // Add location from library to this scene
  const addFromLibraryMutation = useMutation({
    mutationFn: (loc: Location) => {
      const existingPins = pinsData?.pins ?? [];
      if (existingPins.some(p => p.locationId === loc.id)) {
        return Promise.resolve({ success: false as const, error: 'Already in this scene' });
      }
      return api.post('/pins', {
        sceneId: scene.id,
        locationId: loc.id,
        title: loc.name,
        address: loc.address,
        lat: loc.lat,
        lng: loc.lng,
        pinType: 'main',
      });
    },
    onSuccess: (result) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['pins', scene.id] });
      toast.success('Location added');
      setLibraryOpen(false);
    },
  });

  // Remove a pin from this scene
  const removePinMutation = useMutation({
    mutationFn: (pinId: string) => api.delete(`/pins/${pinId}`),
    onSuccess: (_, pinId) => {
      qc.invalidateQueries({ queryKey: ['pins', scene.id] });
      // If removed pin was the active one, clear active
      if (scene.activePinId === pinId) {
        api.put(`/scenes/${scene.id}`, { activePinId: null }).then(() => {
          qc.invalidateQueries({ queryKey: ['scenes', scene.projectId] });
        });
      }
    },
  });

  // Set active location for this scene
  const setActivePinMutation = useMutation({
    mutationFn: (pinId: string | null) => api.put(`/scenes/${scene.id}`, { activePinId: pinId }),
    onSuccess: (_, pinId) => {
      // Optimistically update the scene in the cache
      qc.setQueryData<{ scenes: Scene[] }>(['scenes', scene.projectId], old => {
        if (!old) return old;
        return { ...old, scenes: old.scenes.map(s => s.id === scene.id ? { ...s, activePinId: pinId } : s) };
      });
      // Also update activeScene in mapStore if needed
      if (activeScene?.id === scene.id) {
        setActiveScene({ ...scene, activePinId: pinId });
      }
    },
  });

  const handleClick = () => {
    setActiveScene(isActive ? null : scene);
  };

  const pins = pinsData?.pins ?? [];
  const activePinId = scene.activePinId ?? pins.find(p => p.pinType === 'main')?.id ?? null;

  return (
    <div className={`rounded-lg border transition-all ${isActive ? 'border-brand-400 bg-brand-50' : 'border-gray-200 hover:border-gray-300'}`}>
      {/* Header row */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2.5 cursor-pointer hover:bg-brand-50/50 rounded-t-lg"
        onClick={handleClick}
      >
        <p className="text-sm font-medium text-gray-800 truncate">{scene.name}</p>
        <div className="relative flex gap-1 shrink-0" onClick={e => e.stopPropagation()} ref={pickerRef}>
          {/* Add to day */}
          <button
            onClick={() => setDayPickerOpen(o => !o)}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-brand-500 text-xs"
            title="Add to day"
          >📅</button>
          {dayPickerOpen && (
            <div className="absolute right-0 top-7 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-40 overflow-hidden">
              {!days?.length ? (
                <p className="text-xs text-gray-400 px-3 py-2">No days yet</p>
              ) : (
                <ul className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                  {days.map(d => (
                    <li
                      key={d.id}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-brand-50 cursor-pointer"
                      onClick={() => addToDayMutation.mutate(d.id)}
                    >
                      {d.name}
                      {d.shootDate && <span className="text-xs text-gray-400 ml-1">· {d.shootDate}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <button onClick={() => openModal('sceneForm', scene)} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 text-xs" title="Edit">✎</button>
          <button
            onClick={() => { if (confirm('Delete this scene?')) deleteMutation.mutate(); }}
            className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 text-xs"
            title="Delete"
          >✕</button>
        </div>
      </div>

      {/* Expanded locations section (when scene is active) */}
      {isActive && (
        <div className="border-t border-brand-200 px-3 pb-3 pt-2 space-y-2 bg-white rounded-b-lg">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Locations</p>
            <div className="relative">
              <button
                onClick={() => setLibraryOpen(o => !o)}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium"
              >+ From Library</button>
              {libraryOpen && (
                <div className="absolute right-0 top-6 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-52 overflow-hidden">
                  {!libraryLocs ? (
                    <div className="flex justify-center py-3"><LoadingSpinner size="sm" /></div>
                  ) : libraryLocs.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-2 text-center">No saved locations</p>
                  ) : (
                    <ul className="max-h-52 overflow-y-auto divide-y divide-gray-50">
                      {libraryLocs.map(loc => (
                        <li
                          key={loc.id}
                          className="px-3 py-2 hover:bg-brand-50 cursor-pointer"
                          onClick={() => addFromLibraryMutation.mutate(loc)}
                        >
                          <p className="text-sm font-medium text-gray-800">{loc.businessName || loc.name}</p>
                          <p className="text-xs text-gray-400 truncate">{loc.address}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="border-t border-gray-100 px-3 py-1.5">
                    <button onClick={() => setLibraryOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {pinsLoading ? (
            <div className="flex justify-center py-2"><LoadingSpinner size="sm" /></div>
          ) : pins.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-1">No locations — add from library or <button onClick={() => openModal('pinForm', { sceneId: scene.id })} className="text-brand-500 hover:text-brand-600">create new</button></p>
          ) : (
            <div className="space-y-1">
              {pins.map(pin => {
                const isActiveLoc = pin.id === activePinId;
                return (
                  <div
                    key={pin.id}
                    className={`flex items-center gap-2 rounded-lg px-2.5 py-2 border ${isActiveLoc ? 'border-brand-300 bg-brand-50' : 'border-gray-100 bg-gray-50'}`}
                  >
                    <button
                      onClick={() => setActivePinMutation.mutate(isActiveLoc ? null : pin.id)}
                      title={isActiveLoc ? 'Active location (click to unstar)' : 'Set as active location'}
                      className={`text-base shrink-0 transition-opacity ${isActiveLoc ? 'opacity-100' : 'opacity-30 hover:opacity-70'}`}
                    >⭐</button>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{pin.title}</p>
                      {pin.address && <p className="text-xs text-gray-400 truncate">{pin.address}</p>}
                    </div>
                    <button
                      onClick={() => flyTo(pin.lng, pin.lat)}
                      className="text-gray-400 hover:text-brand-500 text-xs p-0.5"
                      title="Fly to"
                    >🎯</button>
                    <button
                      onClick={() => { if (confirm('Remove this location?')) removePinMutation.mutate(pin.id); }}
                      className="text-gray-300 hover:text-red-500 text-xs p-0.5"
                      title="Remove"
                    >✕</button>
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={() => openModal('pinForm', { sceneId: scene.id })}
            className="w-full text-xs text-brand-600 hover:text-brand-700 font-medium py-1 border border-dashed border-brand-300 hover:border-brand-400 rounded-lg"
          >+ New Location</button>
        </div>
      )}
    </div>
  );
}
